import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { ThemeType } from 'src/theming/jssTypes';
import { pdfjs, Document, Page } from 'react-pdf';
import { Spin, Button } from 'src/design-system/components/atoms';
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  LeftOutlined,
  RightOutlined,
  DownloadOutlined,
  PrinterOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useTheme } from 'src/theming/useTheme';

pdfjs.GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/2.9.359/pdf.worker.js';

const useStyles = createUseStyles((theme: ThemeType) => ({
  pdfToolbar: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing[2],
    padding: `${theme.spacing[2]} ${theme.spacing[0]}`,
    background: theme.color.semantic.background.neutral.subtle,
    borderBottom: `${theme.border.width.sm} solid ${theme.color.semantic.border.secondary}`,
  },
  pdfContainer: {
    height: '80vh',
    overflow: 'auto',
    display: 'flex',
    justifyContent: 'center',
    background: theme.color.semantic.background.neutral.strongL1,
    padding: theme.spacing[4],
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '200px',
  },
  divider: {
    margin: `${theme.spacing[0]} ${theme.spacing[2]}`,
    borderLeft: `${theme.border.width.sm} solid ${theme.color.semantic.border.secondary}`,
    height: '24px',
  },
  zoomText: {
    minWidth: '50px',
    textAlign: 'center',
    color: theme.color.semantic.text.primary,
    fontSize: theme.typography.fontSize.sm,
  },
  pageText: {
    minWidth: '80px',
    textAlign: 'center',
    color: theme.color.semantic.text.primary,
    fontSize: theme.typography.fontSize.sm,
  },
  documentLoader: {
    marginTop: '40%',
  },
}));

export interface PdfRendererProps {
  url: string;
  downloadFileName?: string;
  onError?: () => void;
  onLoadSuccess?: () => void;
  height?: number;
  isLoading?: boolean;
}

const PdfRenderer: React.FC<PdfRendererProps> = (props) => {
  const { url, downloadFileName, onError, onLoadSuccess, height, isLoading } = props;
  const classes = useStyles();
  const theme = useTheme();

  const [numPages, setNumPages] = React.useState<number>(1);
  const [pageNumber, setPageNumber] = React.useState<number>(1);
  const [scale, setScale] = React.useState<number>(1.0);

  const onDocumentLoadSuccess = ({ numPages: totalPages }: { numPages: number }) => {
    setNumPages(totalPages);
    setPageNumber(1);
    onLoadSuccess?.();
  };

  const handleDocumentError = () => {
    onError?.();
  };

  const handlePrevPage = () => {
    if (pageNumber > 1) {
      setPageNumber(pageNumber - 1);
    }
  };

  const handleNextPage = () => {
    if (pageNumber < numPages) {
      setPageNumber(pageNumber + 1);
    }
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  };

  const handleDownload = () => {
    if (url) {
      if (url.startsWith('blob:')) {
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', downloadFileName || 'document.pdf');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handlePrint = () => {
    if (url) {
      const printWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    }
  };

  if (isLoading) {
    return (
      <div className={classes.loadingContainer}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: theme.typography.fontSize.xxxl }} spin />} />
      </div>
    );
  }

  return (
    <div>
      <div className={classes.pdfToolbar}>
        <Button
          icon={<DownloadOutlined />}
          onClick={handleDownload}
          title="Download"
        />
        <Button
          icon={<PrinterOutlined />}
          onClick={handlePrint}
          title="Print"
        />
        <span className={classes.divider} />
        <Button
          icon={<ZoomOutOutlined />}
          onClick={handleZoomOut}
          disabled={scale <= 0.5}
          title="Zoom Out"
        />
        <span className={classes.zoomText}>
          {Math.round(scale * 100)}%
        </span>
        <Button
          icon={<ZoomInOutlined />}
          onClick={handleZoomIn}
          disabled={scale >= 3}
          title="Zoom In"
        />
        <span className={classes.divider} />
        <Button
          icon={<LeftOutlined />}
          onClick={handlePrevPage}
          disabled={pageNumber <= 1}
          title="Previous Page"
        />
        <span className={classes.pageText}>
          {pageNumber} / {numPages}
        </span>
        <Button
          icon={<RightOutlined />}
          onClick={handleNextPage}
          disabled={pageNumber >= numPages}
          title="Next Page"
        />
      </div>
      <div className={classes.pdfContainer}>
        <Document
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={handleDocumentError}
          loading={<Spin className={classes.documentLoader} />}
        >
          <Page
            pageNumber={pageNumber}
            {...(height ? { height } : { scale })}
            renderTextLayer={false}
            renderAnnotationLayer={false}
          />
        </Document>
      </div>
    </div>
  );
};

PdfRenderer.defaultProps = {
  downloadFileName: undefined,
  onError: undefined,
  onLoadSuccess: undefined,
  height: undefined,
  isLoading: false,
};

export default PdfRenderer;
